import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, withCtx as _withCtx } from "vue"

const _hoisted_1 = {
  id: "location_form",
  class: "w-full"
}
const _hoisted_2 = { class: "formgrid grid" }
const _hoisted_3 = {
  key: 0,
  class: "field col pr-3"
}
const _hoisted_4 = { class: "field col" }
const _hoisted_5 = { class: "field" }
const _hoisted_6 = { class: "formgrid grid" }
const _hoisted_7 = { class: "field col" }
const _hoisted_8 = { class: "field col" }
const _hoisted_9 = { class: "field col" }
const _hoisted_10 = { class: "field" }
const _hoisted_11 = { key: 0 }

import { computed, onMounted, ref, toRefs } from 'vue';
import AutoComplete from 'primevue/autocomplete';
import { DropdownChangeEvent } from 'primevue/dropdown';
import useVuelidate from '@vuelidate/core';
import { required, requiredIf, url, helpers } from '@vuelidate/validators';
import { formatPhone, validatePhone } from '@/composables';
import { useCitiesStore, useCountriesStore, useLocationTypesStore, useStatesStore } from '@/store';
import { LocationFormInterface } from '@/types';
import { EMIT } from '@/enum';

interface PropsInterface {
  data: LocationFormInterface;
  loading: boolean;
  isOrganization: boolean;
  isTenant?: boolean;
  permissionToEdit?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'LocationForm',
  props: {
    data: {},
    loading: { type: Boolean },
    isOrganization: { type: Boolean },
    isTenant: { type: Boolean },
    permissionToEdit: { type: Boolean }
  },
  setup(__props: any, { emit: __emit }) {

const props = __props;
const zipcode = helpers.regex(/^\d{5}(-\d{4})?$/);

/**
 * Define all the available emit events.
 */
const emit = __emit;

/**
 * Convert the properties into references.
 */
const { data, loading, isOrganization, isTenant } = toRefs(props);

/**
 * The suggested cities after running a search.
 */
const cities = ref([]);

/**
 * The available states before and after selecting a country.
 */
const states = ref([]);

/**
 * The location reference object to contain the form data.
 */
const location = ref({ ...data.value });

/**
 * All of the state references including the convertion of the
 * location state object into a ref.
 */
const citiesStore = useCitiesStore();
const countriesStore = useCountriesStore();
const locationTypesStore = useLocationTypesStore();
const statesStore = useStatesStore();

/**
 * The required validation rules.
 */
const rules = {
  location_type_id: { requiredIfCustom: requiredIf(isOrganization.value) },
  country_id: { required },
  address: { required },
  city_name: { required },
  state_id: { required },
  zipcode: { required, zipcode: helpers.withMessage('Zipcode must be valid', zipcode) },
  phone: { required, validatePhone },
  website: { requiredIfCustom: requiredIf(isOrganization.value), url },
};

/**
 * Initialize the vuelidate instance.
 */
const v$ = useVuelidate(rules, location);

/**
 * Throw all errors here for easier access.
 */
const errors = ref(null);

/**
 * Initialize everything required to build out the component.
 */
onMounted(() => init());

/**
 * Check if all component resources have completed loading.
 */
const isLoading = computed(() => {
  return (
    loading.value || citiesStore.loading || countriesStore.loading || locationTypesStore.loading || statesStore.loading
  );
});

/**
 * Define the request update payload.
 */
const payload = computed(() => {
  const value = location.value;

  return {
    location_type_id: value.location_type_id,
    country_id: value.country_id,
    address: value.address,
    city_id: value.city_id,
    city_name: value.city_name,
    state_id: value.state_id,
    zipcode: value.zipcode,
    phone: value.phone,
    website: value.website,
  };
});

/**
 * Initialize the location types, countries, states and location.
 */
const init = async () => {
  await locationTypesStore.getLocationTypes();
  await countriesStore.getCountries();
  await statesStore.getStates();
  states.value = statesStore.states;
  onCancel();
};

/**
 * Return a cloned version of the state location object.
 */
const clonedLocation = () => {
  return { ...data.value };
};

/**
 * Reset the vuelidate dirty status for all keys.
 */
const resetDirty = () => v$.value.$reset();

/**
 * Get the field styles based on the specific key.
 */
const fieldStyles = (key: string, styles?: string): string => {
  const validation =
    (v$.value[key].$invalid && v$.value[key].$dirty) || (errors.value && errors.value[key]) ? 'p-invalid' : '';
  return `${styles} ${validation}`;
};

/**
 * Handle the cancellation of all changes made to the location
 * by restting the values and statuses.
 */
const onCancel = () => {
  location.value = clonedLocation();
  errors.value = null;
  resetDirty();
};

/**
 * Handle the client side validation, followed by saving of all changes made
 * to the location.
 */
const onSave = async () => {
  // Don't allow a submit event unless the fields are valid.
  if (v$.value.$invalid) return;

  emit(EMIT.SAVE, payload.value);
  errors.value = null;
  resetDirty();
};

/**
 * Search for a city with the provided city name, state and country identifier.
 * Also, Attempt to pre-populate the state based on the provided city name.
 */
const onCitySearch = async () => {
  if (location.value.country_id && location.value.state_id && location.value.city_name) {
    const params = {
      country_id: location.value.country_id,
      search: location.value.city_name,
    };

    citiesStore.searchCities(params).then((response) => (cities.value = response.data.data));
  } else {
    const response = await citiesStore.searchCities({ search: location.value.city_name });
    const stateIds = response.data.data.map((item) => item.state_id);

    if (stateIds.length === 1) location.value.state_id = stateIds[0];
  }
};

/**
 * Update the city, state and country identifiers once a suggested
 * city has been selected.
 */
const onCityUpdate = (event) => {
  location.value.city_id = event.value.id;
  location.value.city_name = event.value.name;
  location.value.state_id = event.value.state_id;
  location.value.country_id = event.value.country_id;
};

/**
 * Update the listing of states based on the selected country.
 */
const onCountryChange = (event: DropdownChangeEvent) =>
  (states.value = statesStore.states.filter((item) => item.country_id === event.value));

/**
 * Format the phone number when the value has changed.
 */
const onPhone = () => (location.value.phone = formatPhone(location.value.phone as string));

return (_ctx: any,_cache: any) => {
  const _component_Skeleton = _resolveComponent("Skeleton")!
  const _component_Dropdown = _resolveComponent("Dropdown")!
  const _component_InputText = _resolveComponent("InputText")!
  const _component_FormCard = _resolveComponent("FormCard")!

  return (_openBlock(), _createBlock(_component_FormCard, {
    title: "Location",
    dirty: _unref(v$).$anyDirty && __props.permissionToEdit,
    cancel: onCancel,
    save: onSave,
    "save-disabled": _unref(v$).$anyDirty && _unref(v$).$invalid
  }, {
    form: _withCtx(() => [
      _createElementVNode("form", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          (_unref(isOrganization))
            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                _cache[8] || (_cache[8] = _createElementVNode("label", {
                  for: "location_type",
                  class: "requried"
                }, "Location Type", -1)),
                (isLoading.value)
                  ? (_openBlock(), _createBlock(_component_Skeleton, {
                      key: 0,
                      height: "2.3rem"
                    }))
                  : (_openBlock(), _createBlock(_component_Dropdown, {
                      key: 1,
                      id: " location_type",
                      modelValue: _unref(v$).location_type_id.$model,
                      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(v$).location_type_id.$model) = $event)),
                      options: _unref(locationTypesStore).types,
                      "option-label": "title",
                      "option-value": "id",
                      loading: isLoading.value,
                      placeholder: "Select a Type",
                      class: _normalizeClass(fieldStyles('location_type_id')),
                      disabled: !__props.permissionToEdit
                    }, null, 8, ["modelValue", "options", "loading", "class", "disabled"]))
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_4, [
            _cache[9] || (_cache[9] = _createElementVNode("label", {
              for: "country",
              class: "required"
            }, "Country", -1)),
            (isLoading.value)
              ? (_openBlock(), _createBlock(_component_Skeleton, {
                  key: 0,
                  height: "2.3rem"
                }))
              : (_openBlock(), _createBlock(_component_Dropdown, {
                  key: 1,
                  id: "location_country_id",
                  modelValue: _unref(v$).country_id.$model,
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_unref(v$).country_id.$model) = $event)),
                  options: _unref(countriesStore).countries,
                  "option-label": "name",
                  "option-value": "id",
                  loading: isLoading.value,
                  placeholder: "Select a Country",
                  class: _normalizeClass(fieldStyles('country_id')),
                  disabled: !__props.permissionToEdit,
                  onChange: onCountryChange
                }, null, 8, ["modelValue", "options", "loading", "class", "disabled"]))
          ])
        ]),
        _createElementVNode("div", _hoisted_5, [
          _cache[10] || (_cache[10] = _createElementVNode("label", {
            for: "address",
            class: "required"
          }, "Address", -1)),
          (isLoading.value)
            ? (_openBlock(), _createBlock(_component_Skeleton, {
                key: 0,
                height: "2.3rem"
              }))
            : (_openBlock(), _createBlock(_component_InputText, {
                key: 1,
                id: "location_address",
                modelValue: _unref(v$).address.$model,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((_unref(v$).address.$model) = $event)),
                type: "text",
                class: _normalizeClass(fieldStyles('address', 'w-full')),
                placeholder: "Address",
                disabled: !__props.permissionToEdit
              }, null, 8, ["modelValue", "class", "disabled"])),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(v$).address.$errors, (error) => {
            return (_openBlock(), _createElementBlock("div", {
              key: error.$uid,
              class: "input-errors"
            }, [
              _createElementVNode("small", null, _toDisplayString(error.$message), 1)
            ]))
          }), 128))
        ]),
        _createElementVNode("div", _hoisted_6, [
          _createElementVNode("div", _hoisted_7, [
            _cache[11] || (_cache[11] = _createElementVNode("label", {
              for: "city",
              class: "required"
            }, "City", -1)),
            (isLoading.value)
              ? (_openBlock(), _createBlock(_component_Skeleton, {
                  key: 0,
                  height: "2.3rem"
                }))
              : (_openBlock(), _createBlock(_unref(AutoComplete), {
                  key: 1,
                  id: "location_city",
                  modelValue: _unref(v$).city_name.$model,
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((_unref(v$).city_name.$model) = $event)),
                  class: _normalizeClass(fieldStyles('city_name')),
                  field: "name",
                  "min-length": 2,
                  suggestions: cities.value,
                  disabled: !__props.permissionToEdit,
                  onComplete: onCitySearch,
                  onItemSelect: onCityUpdate
                }, null, 8, ["modelValue", "class", "suggestions", "disabled"])),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(v$).city_name.$errors, (error) => {
              return (_openBlock(), _createElementBlock("div", {
                key: error.$uid,
                class: "input-errors"
              }, [
                _createElementVNode("small", null, _toDisplayString(error.$message), 1)
              ]))
            }), 128))
          ]),
          _createElementVNode("div", _hoisted_8, [
            _cache[12] || (_cache[12] = _createElementVNode("label", {
              for: "state",
              class: "required"
            }, "State", -1)),
            (isLoading.value)
              ? (_openBlock(), _createBlock(_component_Skeleton, {
                  key: 0,
                  height: "2.3rem"
                }))
              : (_openBlock(), _createBlock(_component_Dropdown, {
                  key: 1,
                  id: "location_state_id",
                  modelValue: _unref(v$).state_id.$model,
                  "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((_unref(v$).state_id.$model) = $event)),
                  options: states.value,
                  "option-label": "name",
                  "option-value": "id",
                  loading: isLoading.value,
                  placeholder: "Select a State",
                  disabled: !__props.permissionToEdit,
                  class: _normalizeClass(fieldStyles('state_id', 'w-full'))
                }, null, 8, ["modelValue", "options", "loading", "disabled", "class"]))
          ]),
          _createElementVNode("div", _hoisted_9, [
            _cache[13] || (_cache[13] = _createElementVNode("label", {
              for: "zipcode",
              class: "required"
            }, "Zipcode", -1)),
            (isLoading.value)
              ? (_openBlock(), _createBlock(_component_Skeleton, {
                  key: 0,
                  height: "2.3rem"
                }))
              : (_openBlock(), _createBlock(_component_InputText, {
                  key: 1,
                  id: "location_zipcode",
                  modelValue: _unref(v$).zipcode.$model,
                  "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((_unref(v$).zipcode.$model) = $event)),
                  type: "text",
                  class: _normalizeClass(fieldStyles('zipcode')),
                  disabled: !__props.permissionToEdit,
                  placeholder: "Zipcode"
                }, null, 8, ["modelValue", "class", "disabled"])),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(v$).zipcode.$errors, (error) => {
              return (_openBlock(), _createElementBlock("div", {
                key: error.$uid,
                class: "input-errors"
              }, [
                _createElementVNode("small", null, _toDisplayString(error.$message), 1)
              ]))
            }), 128))
          ])
        ]),
        _createElementVNode("div", _hoisted_10, [
          _createElementVNode("label", {
            for: "phone",
            class: _normalizeClass({ required: _unref(isOrganization) })
          }, "Phone", 2),
          (isLoading.value)
            ? (_openBlock(), _createBlock(_component_Skeleton, {
                key: 0,
                height: "2.3rem"
              }))
            : (_openBlock(), _createBlock(_component_InputText, {
                key: 1,
                id: "location_phone",
                modelValue: _unref(v$).phone.$model,
                "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((_unref(v$).phone.$model) = $event)),
                type: "text",
                class: _normalizeClass(fieldStyles('phone')),
                maxlength: "12",
                disabled: !__props.permissionToEdit,
                placeholder: "999-999-9999",
                onInput: onPhone
              }, null, 8, ["modelValue", "class", "disabled"])),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(v$).phone.$errors, (error) => {
            return (_openBlock(), _createElementBlock("div", {
              key: error.$uid,
              class: "input-errors"
            }, [
              _createElementVNode("small", null, _toDisplayString(error.$message), 1)
            ]))
          }), 128))
        ]),
        (_unref(isOrganization) || _unref(isTenant))
          ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
              _createElementVNode("label", {
                for: "website",
                class: _normalizeClass({ required: _unref(isOrganization) })
              }, "Website", 2),
              (isLoading.value)
                ? (_openBlock(), _createBlock(_component_Skeleton, {
                    key: 0,
                    height: "2.3rem"
                  }))
                : (_openBlock(), _createBlock(_component_InputText, {
                    key: 1,
                    id: "location_website",
                    modelValue: _unref(v$).website.$model,
                    "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((_unref(v$).website.$model) = $event)),
                    type: "text",
                    disabled: !__props.permissionToEdit,
                    class: _normalizeClass(fieldStyles('website')),
                    placeholder: "Website"
                  }, null, 8, ["modelValue", "disabled", "class"])),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(v$).website.$errors, (error) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: error.$uid,
                  class: "input-errors"
                }, [
                  _createElementVNode("small", null, _toDisplayString(error.$message), 1)
                ]))
              }), 128))
            ]))
          : _createCommentVNode("", true)
      ])
    ]),
    _: 1
  }, 8, ["dirty", "save-disabled"]))
}
}

})